import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { useEffect } from "react";

// Layout
import AdminLayout from "@/components/layout/AdminLayout";

// Pages
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import DestinationsPage from "@/pages/destinations/DestinationsPage";
import DestinationEditPage from "@/pages/destinations/DestinationEditPage";
import GuidesPage from "@/pages/guides/GuidesPage";
import GuideEditPage from "@/pages/guides/GuideEditPage";
import TripsPage from "@/pages/trips/TripsPage";
import TripEditPage from "@/pages/trips/TripEditPage";
import OrdersPage from "@/pages/orders/OrdersPage";
import OrderDetailPage from "@/pages/orders/OrderDetailPage";
import OrderKanbanPage from "@/pages/orders/OrderKanbanPage";
import NewOrderPage from "@/pages/orders/NewOrderPage";
import CustomersPage from "@/pages/customers/CustomersPage";
import CustomerDetailPage from "@/pages/customers/CustomerDetailPage";
import NewCustomerPage from "@/pages/customers/NewCustomerPage";
import NewsletterPage from "@/pages/newsletter/NewsletterPage";
import CampaignEditorPage from "@/pages/newsletter/CampaignEditorPage";
import ReviewsPage from "@/pages/reviews/ReviewsPage";
import ReviewEditPage from "@/pages/reviews/ReviewEditPage";
import MediaPage from "@/pages/MediaPage";
import SegmentsPage from "@/pages/segments/SegmentsPage";
import AutomationsPage from "@/pages/automations/AutomationsPage";
import UsersPage from "@/pages/users/UsersPage";
import TemplatesPage from "@/pages/templates/TemplatesPage";
import TemplateEditPage from "@/pages/templates/TemplateEditPage";
import WorkflowsPage from "@/pages/workflows/WorkflowsPage";
import WorkflowEditPage from "@/pages/workflows/WorkflowEditPage";
import AnalyticsPage from "@/pages/analytics/AnalyticsPage";
import SettingsPage from "@/pages/SettingsPage";
import ContactMessagesPage from "@/pages/messages/ContactMessagesPage";

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading, checkAuth } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />

          {/* Destinations */}
          <Route path="destinations" element={<DestinationsPage />} />
          <Route path="destinations/new" element={<DestinationEditPage />} />
          <Route path="destinations/:id" element={<DestinationEditPage />} />

          {/* Guides */}
          <Route path="guides" element={<GuidesPage />} />
          <Route path="guides/new" element={<GuideEditPage />} />
          <Route path="guides/:id" element={<GuideEditPage />} />

          {/* Trips */}
          <Route path="trips" element={<TripsPage />} />
          <Route path="trips/new" element={<TripEditPage />} />
          <Route path="trips/:id" element={<TripEditPage />} />

          {/* Orders */}
          <Route path="orders" element={<OrdersPage />} />
          <Route path="orders/new" element={<NewOrderPage />} />
          <Route path="orders/kanban" element={<OrderKanbanPage />} />
          <Route path="orders/:id" element={<OrderDetailPage />} />

          {/* Customers */}
          <Route path="customers" element={<CustomersPage />} />
          <Route path="customers/new" element={<NewCustomerPage />} />
          <Route path="customers/:id" element={<CustomerDetailPage />} />

          {/* Newsletter */}
          <Route path="newsletter" element={<NewsletterPage />} />
          <Route path="newsletter/editor/:id" element={<CampaignEditorPage />} />

          {/* Segments */}
          <Route path="segments" element={<SegmentsPage />} />

          {/* Automations */}
          <Route path="automations" element={<AutomationsPage />} />

          {/* Reviews */}
          <Route path="reviews" element={<ReviewsPage />} />
          <Route path="reviews/new" element={<ReviewEditPage />} />
          <Route path="reviews/:id" element={<ReviewEditPage />} />

          {/* Media */}
          <Route path="media" element={<MediaPage />} />

          {/* Users */}
          <Route path="users" element={<UsersPage />} />

          {/* Templates */}
          <Route path="templates" element={<TemplatesPage />} />
          <Route path="templates/new" element={<TemplateEditPage />} />
          <Route path="templates/:id" element={<TemplateEditPage />} />

          {/* Workflows */}
          <Route path="workflows" element={<WorkflowsPage />} />
          <Route path="workflows/new" element={<WorkflowEditPage />} />
          <Route path="workflows/:id" element={<WorkflowEditPage />} />

          {/* Analytics */}
          <Route path="analytics" element={<AnalyticsPage />} />

          {/* Contact Messages */}
          <Route path="messages" element={<ContactMessagesPage />} />

          {/* Settings */}
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
